import { clsx } from "clsx";
import type { QuestionOption, SanitizedQuestionItem } from "../../../shared/types";
import { AnswerOptionRenderer } from "./AnswerOptionRenderer";

function getOptionImages(question: SanitizedQuestionItem) {
  const optionImages = question.visualSpec.optionImages;
  if (!optionImages || typeof optionImages !== "object") return null;
  return optionImages as Record<string, string>;
}

function getOptionType(option: QuestionOption) {
  const visual = option.visual ?? option.visualSpec;
  return option.type ?? (typeof visual?.kind === "string" ? visual.kind : undefined);
}

function hasVisualOptions(options: QuestionOption[]) {
  return options.some((option) => {
    const type = getOptionType(option);
    return Boolean(type && type !== "text" && type !== "number") || Boolean(option.visual?.shape ?? option.visualSpec?.shape);
  });
}

function hasLongTextOptions(options: QuestionOption[]) {
  return options.some((option) => {
    const type = getOptionType(option);
    const text = String(option.value ?? option.label ?? "");
    return (type === "text" || !type) && text.length > 28;
  });
}

export function AnswerGrid({
  question,
  selectedOption,
  onSelect,
  readOnly = false,
  revealCorrectOption
}: {
  question: SanitizedQuestionItem;
  selectedOption?: string | null;
  onSelect?: (optionId: string) => void;
  readOnly?: boolean;
  revealCorrectOption?: string;
}) {
  if (question.visualSpec.interaction === "directSelection") return null;

  const optionImages = getOptionImages(question);
  const visual = Boolean(optionImages) || hasVisualOptions(question.options);
  const longText = hasLongTextOptions(question.options);

  return (
    <div
      className={clsx("mt-4 grid gap-3", visual ? "grid-cols-2" : longText ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2")}
      role="radiogroup"
      aria-label="Επιλογές απάντησης"
    >
      {question.options.map((option, index) => {
        const selected = selectedOption === option.id;
        const correct = revealCorrectOption === option.id;
        const imageSrc = optionImages?.[option.id];

        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={selected || correct}
            disabled={readOnly}
            onClick={() => {
              if (!readOnly) onSelect?.(option.id);
            }}
            className={clsx(
              "flex min-h-16 items-center gap-3 rounded-lg border p-4 text-left transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-200",
              correct
                ? "border-emerald-200/80 bg-emerald-200/12 shadow-[0_0_26px_rgb(110_231_183_/_0.14)]"
                : selected
                  ? "border-cyan-200/80 bg-cyan-200/12 shadow-[0_0_26px_rgb(34_211_238_/_0.16)]"
                  : "border-white/10 bg-white/[0.045] hover:border-cyan-200/45 hover:bg-white/[0.07]",
              readOnly ? "cursor-default hover:border-white/10 hover:bg-white/[0.045]" : ""
            )}
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/15 bg-slate-950/45 text-sm font-bold text-cyan-100">
              {index + 1}
            </span>
            <span className="flex min-w-0 flex-1 items-center justify-center text-base font-semibold text-white">
              <AnswerOptionRenderer option={option} imageSrc={imageSrc} />
            </span>
          </button>
        );
      })}
    </div>
  );
}
